/**
 * To / Cc / Bcc as chips. Type a name or address: ONE's people and the people you write with are suggested; Enter,
 * comma or Tab adds what you typed when it's an address. Backspace on an empty box removes the last chip.
 */
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";

const EMAIL = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
export interface Person { name: string | null; email: string; inOne?: boolean }

export function RecipientInput({ label, value, onChange, names, onName, autoFocus, extra }: {
  label: string; value: string[]; onChange: (v: string[]) => void; names: Record<string, string>; onName: (email: string, name: string) => void; autoFocus?: boolean; extra?: React.ReactNode;
}) {
  const [text, setText] = useState("");
  const [focus, setFocus] = useState(false);
  const [hi, setHi] = useState(0);
  const q = text.trim();
  const sug = useQuery({ queryKey: ["mail-people", q], queryFn: () => api<{ items: Person[] }>(`/api/mail/people?q=${encodeURIComponent(q)}`), enabled: focus && q.length >= 2, staleTime: 30_000 });
  const items = (sug.data?.items ?? []).filter((p) => !value.includes(p.email)).slice(0, 8);
  const add = (email: string, name?: string | null) => {
    const e = email.trim().toLowerCase();
    if (!EMAIL.test(e) || value.includes(e)) { setText(""); return; }
    if (name) onName(e, name);
    onChange([...value, e]);
    setText("");
    setHi(0);
  };
  const commit = () => { if (items[hi] && q && !EMAIL.test(q)) add(items[hi]!.email, items[hi]!.name); else if (EMAIL.test(q)) add(q); };
  return (
    <div className="relative flex min-h-9 flex-wrap items-center gap-1 border-b border-[var(--line)] py-1 text-sm">
      <span className="muted w-9 shrink-0 text-xs">{label}</span>
      {value.map((e) => (
        <span key={e} className="chip max-w-full gap-1 !py-0.5 text-xs" title={e}>
          <span className="truncate">{names[e] ?? e}</span>
          <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} aria-label={`Remove ${e}`}><X size={11} /></button>
        </span>
      ))}
      <input
        className="min-w-[10rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none" value={text} autoFocus={autoFocus} aria-label={`${label} recipients`}
        placeholder={value.length ? "" : "Name or email…"}
        onChange={(e) => { const v = e.target.value; if (/[,;\s]$/.test(v) && EMAIL.test(v.slice(0, -1).trim())) add(v.slice(0, -1)); else { setText(v); setHi(0); } }}
        onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => { setFocus(false); if (EMAIL.test(text.trim())) add(text); }, 150)}
        onPaste={(e) => { const t = e.clipboardData.getData("text"); const list = t.split(/[,;\s]+/).filter((x) => EMAIL.test(x)); if (list.length > 1) { e.preventDefault(); onChange([...new Set([...value, ...list.map((x) => x.toLowerCase())])]); } }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Tab" && q) { if (q) { e.preventDefault(); commit(); } }
          else if (e.key === "Backspace" && !text && value.length) onChange(value.slice(0, -1));
          else if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        }}
      />
      {extra}
      {focus && q.length >= 2 && items.length > 0 && (
        <ul className="panel absolute left-9 top-full z-30 mt-1 w-72 max-w-[90vw] overflow-hidden p-1 shadow-xl">
          {items.map((p, i) => (
            <li key={p.email}>
              <button type="button" className={`block w-full truncate rounded px-2 py-1.5 text-left text-sm ${i === hi ? "bg-black/[0.06]" : "hover:bg-black/[0.04]"}`} onMouseDown={(e) => { e.preventDefault(); add(p.email, p.name); }}>
                {p.name ? <><span className="font-medium">{p.name}</span> <span className="muted text-xs">{p.email}</span></> : p.email}
                {p.inOne && <span className="chip ml-1 !py-0 text-[10px]">ONE</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
